import getSdkSession from './utils/getSdkSession';

const Profile = {

    getSdkSession,

    /**
     * @description Gets the active profile.
     * @returns {Promise<Object>}
     */
    async getActiveProfile() {

        const { userProfileApi } = await Profile.getSdkSession();

        return userProfileApi.getActiveUserProfile();

    },

    /**
     * @description Gets all the account profiles.
     * @returns {Promise<Array>}
     */
    async getProfiles() {

        const { userProfileApi } = await Profile.getSdkSession();

        return userProfileApi.getUserProfiles();
    },

    /**
     * @description Gets a profile from userProfileApi.
     * @param {String} id - the profile id
     * @returns {Promise<Object>} - the user profile
     */
    async getProfile(id) {

        if (!id || (typeof id !== 'string')) {
            throw new Error('You must pass an id string to getProfile.');
        }

        const { userProfileApi } = await Profile.getSdkSession();

        return userProfileApi.getUserProfile(id);

    },

    /**
     * @description Creates a new user profile.
     * @note Both attributes and metadata are optional parameters.
     * @param {String} name - profile name
     * @param {Object} attributes - optional profile attributes
     * @param {Object} metadata - optional metadata
     * @returns {Promise<Object>} - returns a user profile
     */
    async createProfile(name, attributes, metadata) {

        if (!name || (typeof name !== 'string')) {
            throw new Error('You must pass a name string to createProfile.');
        }

        if (
            (attributes && (typeof attributes !== 'object')) ||
            (metadata && (typeof metadata !== 'object'))
        ) {
            throw new Error('If you pass attributes or metadata to createProfile they must type object.');
        }
        const { userProfileApi } = await Profile.getSdkSession();

        return userProfileApi.createUserProfile(name.trim(), attributes, metadata);

    },

    /**
     * @description Sets the active user profile.
     * @param {Object} profile - the profile to set active
     * @returns {Promise}
     */
    async setActiveProfile(profile) {

        if (!profile || (typeof profile !== 'object')) {
            throw new Error('You must pass a profile object to setActiveProfile.');
        }

        const { userProfileApi } = await Profile.getSdkSession();

        return userProfileApi.setActiveUserProfile(profile);

    },

    /**
     * @description Delete a profile.
     * @param {Object} profile - the profile to delete
     * @return {Promise}
     */
    async deleteProfile(profile) {

        if (!profile || (typeof profile !== 'object')) {
            throw new Error('You must pass a profile object to deleteProfile.');
        }

        const { userProfileApi } = await Profile.getSdkSession();

        return userProfileApi.deleteUserProfile(profile);

    },

    /**
     * @description Updates a profile's attributes.
     * @param {String} id - profile id
     * @param {Object} attributes - attributes to update
     * @param {Class} UserProfile - have to inject this here until SDK updated
     * @returns {Promise}
     */
    async updateAttributes(id, attributes, UserProfile) {

        if (
            !id ||
            (typeof id !== 'string') ||
            !attributes ||
            (typeof attributes !== 'object')
        ) {
            throw new Error('You must pass an id string and attributes object to updateAttributes.');
        }

        const sdkSession = await Profile.getSdkSession();
        const { userProfileApi } = sdkSession;
        const initialProfile = await Profile.getProfile(id);
        const updatedAttributes = { ...initialProfile.attributes, ...attributes };
        const updatedProfile = new UserProfile({ ...initialProfile, attributes: updatedAttributes });

        return userProfileApi.updateUserProfile(updatedProfile);

    },

    /**
     * @description Updates a profile name.
     * @param {String} id - profile id
     * @param {String} newName - new profile name
     * @param {Class} UserProfile - have to inject this here until SDK updated
     * @returns {Promise}
     */
    async updateName(id, newName, UserProfile) {

        if (
            !id ||
            (typeof id !== 'string') ||
            !newName ||
            (typeof newName !== 'string')
        ) {
            throw new Error('You must pass an id string and newName string to updateName.');
        }

        const sdkSession = await Profile.getSdkSession();
        const { userProfileApi } = sdkSession;
        const initialProfile = await Profile.getProfile(id);
        const updatedProperties = { ...initialProfile, profileName: newName.trim() };
        const updatedProfile = new UserProfile(updatedProperties);

        return userProfileApi.updateUserProfile(updatedProfile);

    }

};

export default Profile;
